/**
 * MislabeledProductsDialog
 *
 * Lists catalog products whose name / subcategory hints at one
 * `main_category` but are filed under another. One-click "Fix" applies
 * the suggested category. Pure read-only scan unless the admin clicks
 * Fix — no auto-write magic.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { api } from '../../lib/store';
import { toast } from 'sonner';
import { Loader2, Tags, CheckCircle2, Wand2 } from 'lucide-react';

export default function MislabeledProductsDialog({ open, onOpenChange }) {
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(0);
  const [candidates, setCandidates] = useState(null);
  const [fixingId, setFixingId] = useState(null);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await api.findMislabeledProducts();
      setScanned(res.data.total_scanned);
      setCandidates(res.data.candidates || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleFix = async (c) => {
    setFixingId(c.id);
    try {
      await api.fixMislabeledProduct(c.id, c.suggested_category);
      toast.success(`${c.brand_name} → ${c.suggested_category}`);
      setCandidates((prev) => prev.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fix failed');
    } finally {
      setFixingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" data-testid="mislabeled-products-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="w-5 h-5 text-heda-gold" /> Find Mislabeled Products
          </DialogTitle>
          <p className="text-xs text-heda-sub mt-1">
            Scans your catalog for products whose name/subcategory suggests a different category. Fixes are one-click and reversible (edit the product again to revert).
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-3">
          {candidates === null && (
            <div className="border border-dashed border-heda-line rounded-xl p-6 text-center text-sm text-heda-sub">
              Click <strong>Run Scan</strong> to find products whose category doesn&apos;t match their name.
            </div>
          )}
          {scanning && (
            <div className="flex items-center justify-center gap-2 text-sm text-heda-sub py-6">
              <Loader2 className="w-4 h-4 animate-spin text-heda-gold" /> Scanning {scanned > 0 ? `${scanned}` : 'all'} products…
            </div>
          )}
          {candidates !== null && !scanning && (
            <>
              <div className="text-xs text-heda-sub">
                Scanned <strong className="text-heda-ink tabular-nums">{scanned}</strong> products · Found <strong className={candidates.length > 0 ? 'text-amber-700' : 'text-emerald-700'}>{candidates.length}</strong> candidates
              </div>
              {candidates.length === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-700 flex items-center gap-2" data-testid="mislabeled-allclean">
                  <CheckCircle2 className="w-4 h-4" /> Your catalog is clean — no mislabeled products found.
                </div>
              ) : (
                <div className="border border-heda-line rounded-xl overflow-x-auto bg-white">
                  <table className="w-full text-left text-sm">
                    <thead className="heda-thead">
                      <tr>
                        <th className="px-3 sm:px-4 py-2.5 font-semibold text-left">Product</th>
                        <th className="px-3 sm:px-4 py-2.5 font-semibold text-left">Current</th>
                        <th className="px-3 sm:px-4 py-2.5 font-semibold text-left">Suggested</th>
                        <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map((c) => (
                        <tr key={c.id} className="border-t border-heda-line" data-testid={`mislabeled-row-${c.id}`}>
                          <td className="px-3 sm:px-4 py-2.5">
                            <p className="font-semibold text-heda-ink">{c.brand_name}</p>
                            <p className="text-[11px] text-heda-mute">{c.subcategory} {c.thickness && `· ${c.thickness}`}</p>
                          </td>
                          <td className="px-3 sm:px-4 py-2.5">
                            <Badge variant="outline" className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-none border-transparent text-red-700 bg-red-50">{c.current_category}</Badge>
                          </td>
                          <td className="px-3 sm:px-4 py-2.5">
                            <Badge variant="outline" className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-none border-transparent text-emerald-700 bg-emerald-50">{c.suggested_category}</Badge>
                          </td>
                          <td className="px-3 sm:px-4 py-2.5 text-right">
                            <Button
                              size="sm"
                              className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black h-8 text-xs"
                              onClick={() => handleFix(c)}
                              disabled={fixingId === c.id}
                              data-testid={`mislabeled-fix-${c.id}`}
                            >
                              {fixingId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Wand2 className="w-3 h-3 mr-1" /> Fix</>}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t border-heda-line gap-2">
          <Button className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" onClick={() => onOpenChange(false)} disabled={scanning} data-testid="mislabeled-close-btn">
            Close
          </Button>
          <Button
            onClick={runScan}
            disabled={scanning}
            className="rounded-xl bg-heda-olive text-white shadow-none hover:bg-heda-olive-dark"
            data-testid="mislabeled-scan-btn"
          >
            {scanning ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Scanning…</> : (candidates === null ? 'Run Scan' : 'Re-scan')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
